// @ts-nocheck
// TODO: align with current types (Phase 4a) — bucket 2/3 + DS strictness regressions
/**
 * Assign Access Modal
 *
 * Modal dialog for assigning a direct access object (permission) to a user,
 * outside of any role. Supports company context and expiry date.
 *
 * Mirrors AssignRoleModal, but targets the user direct-access surface
 * (/api/admin/users/:userId/access) and lists access objects instead of roles.
 */

import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@lognai/synapse-design-system-v2';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import { Input } from '@lognai/synapse-design-system-v2';
import { Checkbox } from '@lognai/synapse-design-system-v2';
import { useToast } from '@lognai/synapse-design-system-v2';
import { FormModal, SearchableSelect } from '../shared';
import { useAccessObjects, useCompanies, useAssignUserAccess } from '../../hooks';
import type { AccessObject } from '@lognai/plugin-sdk';

/** Sentinel for "no company context" — Radix Select disallows empty-string item values. */
const PLATFORM_WIDE = '__platform_wide__';

const assignAccessSchema = z.object({
  accessId: z.string().min(1, 'Access object is required'),
  companyId: z.string().optional(),
  expiresAt: z.string().optional(),
  hasExpiry: z.boolean().default(false),
});

type AssignAccessFormData = z.infer<typeof assignAccessSchema>;

export interface AssignAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
  onSuccess?: () => void;
}

export function AssignAccessModal({
  open,
  onOpenChange,
  userId,
  userName,
  onSuccess,
}: AssignAccessModalProps) {
  const { toast } = useToast();
  const { data: accessObjects = [], isLoading: accessLoading } = useAccessObjects({
    filter: { isActive: true },
  });
  const { data: companiesData, isLoading: companiesLoading } = useCompanies();
  const companies = companiesData?.items ?? [];
  const assignAccess = useAssignUserAccess();

  const form = useForm<AssignAccessFormData>({
    resolver: zodResolver(assignAccessSchema),
    defaultValues: {
      accessId: '',
      companyId: '',
      expiresAt: '',
      hasExpiry: false,
    },
  });

  const hasExpiry = form.watch('hasExpiry');

  // Flatten access objects into type-ahead options; category + code go in the
  // description so search matches name, code, or category.
  const accessOptions = React.useMemo(
    () =>
      [...accessObjects]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((access) => ({
          value: access.id,
          label: access.name,
          description: `${access.category} · ${access.code}`,
        })),
    [accessObjects]
  );

  const handleSubmit = async (data: AssignAccessFormData) => {
    try {
      const assignment = {
        accessId: data.accessId,
        companyId: data.companyId || undefined,
        expiresAt: data.hasExpiry && data.expiresAt ? new Date(data.expiresAt) : undefined,
      };

      await assignAccess.mutateAsync({ userId, assignment });

      toast({
        title: 'Access granted',
        description: `Successfully granted access to ${userName || 'user'}`,
      });

      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to grant access',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Grant Direct Access"
      description={`Grant a direct permission to ${userName || 'this user'}`}
      size="md"
      submitLabel="Grant Access"
      isSubmitting={assignAccess.isPending}
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Access Object Selection */}
          <FormField
            control={form.control}
            name="accessId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Access Object *</FormLabel>
                <FormControl>
                  <SearchableSelect
                    options={accessOptions}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select an access object"
                    searchPlaceholder="Search by name, code, or category..."
                    emptyText="No access objects found."
                    disabled={accessLoading}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>
                  Grant a single permission directly, independent of roles
                </FormDescription>
              </FormItem>
            )}
          />

          {/* Company Context (Optional) */}
          <FormField
            control={form.control}
            name="companyId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company Context (Optional)</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(value === PLATFORM_WIDE ? '' : value)}
                  value={field.value || PLATFORM_WIDE}
                  disabled={companiesLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Platform-wide (all companies)" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={PLATFORM_WIDE}>Platform-wide (all companies)</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
                <FormDescription>
                  Leave empty for platform-wide access, or scope it to a specific company
                </FormDescription>
              </FormItem>
            )}
          />

          {/* Expiry Date Toggle */}
          <FormField
            control={form.control}
            name="hasExpiry"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Set expiry date</FormLabel>
                  <FormDescription>
                    Access will automatically expire after the specified date
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {/* Expiry Date Input */}
          {hasExpiry && (
            <FormField
              control={form.control}
              name="expiresAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expiry Date *</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                  <FormDescription>
                    Access assignment will expire at the end of this date
                  </FormDescription>
                </FormItem>
              )}
            />
          )}
        </form>
      </Form>
    </FormModal>
  );
}
